import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { normalizeFavorites, createFavorite, inferFavoriteMode, FAVORITE_MODE_AUTO } from '../utils/favorites';

const UserPreferencesContext = createContext(null);

const DEFAULT_PREFS = {
  favorites: [],
  favoriteStoreNames: [],
  petsFilter: 'all',
  jobTitle: '',
  ffMinPrice: 0,
  ffMaxPrice: 6000,
  ffMinBeds: 0,
  ffMinBaths: 0,
  crimeLayerOpacity: 0.45,
  showCrimeLayer: true,
  mapLayer: 'default',
  showStoreMarkers: true,
  showGymMarkers: true,
  showThingsToDo: false,
  showFFMarkers: true,
  /** Master "Rentals" toggle — shows/hides both scraped rentals and housing requests together. */
  showRentals: true,
  /** Custom CSV tenant listings on map — only honored when REACT_APP_ENABLE_TENANT_CUSTOM_LISTINGS=true */
  showTenantCustomListings: false,
  /** Property URLs of housing requests marked inactive (shown gray and smaller on the map). */
  inactiveTenantListings: [],
  /** Property URLs of favorited housing requests (small heart on the map pin). */
  favoriteTenantListings: [],
};

const MAX_INACTIVE_TENANT_LISTINGS = 5000;

function toggleInList(list, value) {
  const current = list || [];
  return current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value].slice(-MAX_INACTIVE_TENANT_LISTINGS);
}

function validateStringList(value, max) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((v) => typeof v === 'string' && v.trim()))].slice(0, max);
}

const GUEST_STORAGE_KEY = 'dwellia_guest_preferences';

/** Per-user localStorage key so each account has its own cache. */
const userCacheKey = (uid) => `dwellia_prefs_${uid}`;

function clampNumber(value, min, max, fallback) {
  if (typeof value !== 'number' || Number.isNaN(value)) return fallback;
  return Math.max(min, Math.min(max, value));
}

function validateBool(value, fallback) {
  return typeof value === 'boolean' ? value : fallback;
}

/** Validate and sanitize raw preference data against DEFAULT_PREFS schema. */
function validatePrefs(raw) {
  if (!raw || typeof raw !== 'object') return DEFAULT_PREFS;
  const favorites = normalizeFavorites(
    Array.isArray(raw.favorites) && raw.favorites.length > 0
      ? raw.favorites
      : raw.favoriteStoreNames
  );

  return {
    favorites,
    favoriteStoreNames: favorites.map((f) => f.label),
    petsFilter:
      raw.petsFilter === 'pets' || raw.petsFilter === 'no-pets'
        ? raw.petsFilter
        : DEFAULT_PREFS.petsFilter,
    ffMinPrice: clampNumber(raw.ffMinPrice, 0, 20000, DEFAULT_PREFS.ffMinPrice),
    ffMaxPrice: clampNumber(raw.ffMaxPrice, 0, 20000, DEFAULT_PREFS.ffMaxPrice),
    ffMinBeds: clampNumber(raw.ffMinBeds, 0, 10, DEFAULT_PREFS.ffMinBeds),
    ffMinBaths: clampNumber(raw.ffMinBaths, 0, 10, DEFAULT_PREFS.ffMinBaths),
    crimeLayerOpacity: clampNumber(raw.crimeLayerOpacity, 0, 1, DEFAULT_PREFS.crimeLayerOpacity),
    showCrimeLayer: validateBool(raw.showCrimeLayer, DEFAULT_PREFS.showCrimeLayer),
    mapLayer: raw.mapLayer === 'satellite' ? 'satellite' : DEFAULT_PREFS.mapLayer,
    showStoreMarkers: validateBool(raw.showStoreMarkers, DEFAULT_PREFS.showStoreMarkers),
    showGymMarkers: validateBool(raw.showGymMarkers, DEFAULT_PREFS.showGymMarkers),
    showThingsToDo: validateBool(raw.showThingsToDo, DEFAULT_PREFS.showThingsToDo),
    showFFMarkers: validateBool(raw.showFFMarkers, DEFAULT_PREFS.showFFMarkers),
    showRentals: validateBool(raw.showRentals, DEFAULT_PREFS.showRentals),
    showTenantCustomListings: validateBool(
      raw.showTenantCustomListings,
      DEFAULT_PREFS.showTenantCustomListings
    ),
    inactiveTenantListings: validateStringList(raw.inactiveTenantListings, MAX_INACTIVE_TENANT_LISTINGS),
    favoriteTenantListings: validateStringList(raw.favoriteTenantListings, MAX_INACTIVE_TENANT_LISTINGS),
    jobTitle: ['travel_nurse', 'travel_ir_tech'].includes(raw.jobTitle)
      ? raw.jobTitle
      : DEFAULT_PREFS.jobTitle,
  };
}

function loadGuestPrefs() {
  try {
    const raw = localStorage.getItem(GUEST_STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    return validatePrefs(JSON.parse(raw));
  } catch (_) {}
  return DEFAULT_PREFS;
}

function saveGuestPrefs(prefs) {
  try {
    localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(prefs));
  } catch (_) {}
}

function userDocRef(uid) {
  return uid ? doc(db, 'users', uid) : null;
}

function loadUserCache(uid) {
  try {
    const raw = localStorage.getItem(userCacheKey(uid));
    if (raw) return validatePrefs(JSON.parse(raw));
  } catch (_) {}
  return null;
}

function saveUserCache(uid, prefs) {
  try {
    localStorage.setItem(userCacheKey(uid), JSON.stringify(prefs));
  } catch (_) {}
}

export function UserPreferencesProvider({ children }) {
  const { user } = useAuth();
  const [prefs, setPrefsState] = useState(() =>
    user ? (loadUserCache(user.uid) ?? DEFAULT_PREFS) : loadGuestPrefs()
  );
  const [loading] = useState(false); // never block UI waiting for Firestore
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (!user?.uid) {
      setPrefsState(loadGuestPrefs());
      return;
    }

    // Show cached prefs immediately so the UI is never frozen waiting for Firestore.
    // Firestore is tried in the background; if it responds it updates state + cache.
    // If it's blocked by an ad blocker the SDK retries for ~60s then rejects — the
    // .catch() handles that silently and the user just sees their local cache.
    const cached = loadUserCache(user.uid);
    if (cached) setPrefsState(cached);

    let cancelled = false;
    getDoc(userDocRef(user.uid))
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists()) {
          const fresh = validatePrefs(snap.data());
          setPrefsState(fresh);
          saveUserCache(user.uid, fresh); // keep cache in sync
        }
      })
      .catch(() => {}); // blocked ad blocker or offline — local cache covers it

    return () => { cancelled = true; };
  }, [user?.uid]);

  const persist = useCallback(
    (nextPrefs) => {
      if (user?.uid) {
        // Always update localStorage first — instant and works even when Firestore
        // is blocked by an ad blocker. Firestore is best-effort on top of that.
        saveUserCache(user.uid, nextPrefs);
        setDoc(userDocRef(user.uid), nextPrefs, { merge: true }).catch(() => {});
      } else {
        saveGuestPrefs(nextPrefs);
      }
    },
    [user?.uid]
  );

  const setPrefs = useCallback(
    (updater) => {
      setPrefsState((prev) => {
        const next =
          typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };

        // Write to local cache immediately so refreshes right after a change
        // never lose the updated preference. Firestore sync remains slightly
        // debounced to avoid excessive writes.
        if (user?.uid) {
          saveUserCache(user.uid, next);
        } else {
          saveGuestPrefs(next);
        }

        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => persist(next), 300);
        return next;
      });
    },
    [persist, user?.uid]
  );

  const addFavorite = useCallback(
    (favoriteInput) => {
      const favorite = typeof favoriteInput === 'string'
        ? createFavorite({ label: favoriteInput, mode: FAVORITE_MODE_AUTO })
        : createFavorite(favoriteInput);
      if (!favorite.label) return;
      setPrefs((prev) => {
        const exists = (prev.favorites || []).some((item) => {
          if (favorite.placeId && item.placeId) return item.placeId === favorite.placeId;
          return item.mode === favorite.mode && item.label.toLowerCase() === favorite.label.toLowerCase();
        });
        if (exists) return prev;
        const nextFavorites = [...(prev.favorites || []), favorite];
        return {
          ...prev,
          favorites: nextFavorites,
          favoriteStoreNames: nextFavorites.map((f) => f.label),
        };
      });
    },
    [setPrefs]
  );

  const updateFavorite = useCallback(
    (favoriteInput, updates) => {
      const target = typeof favoriteInput === 'string'
        ? { label: favoriteInput }
        : favoriteInput;
      setPrefs((prev) => {
        const nextFavorites = (prev.favorites || []).map((item) => {
          const match = target.placeId && item.placeId
            ? item.placeId === target.placeId
            : item.label.toLowerCase() === String(target.label || '').toLowerCase() && (!target.mode || item.mode === target.mode);
          return match ? createFavorite({ ...item, ...updates, placeId: '', id: undefined }) : item;
        });
        return {
          ...prev,
          favorites: nextFavorites,
          favoriteStoreNames: nextFavorites.map((f) => f.label),
        };
      });
    },
    [setPrefs]
  );

  const classifyFavorite = useCallback(
    (favoriteInput) => {
      const target = typeof favoriteInput === 'string'
        ? { label: favoriteInput }
        : favoriteInput;
      setPrefs((prev) => {
        let changed = false;
        const nextFavorites = (prev.favorites || []).map((item) => {
          const match = target.placeId && item.placeId
            ? item.placeId === target.placeId
            : item.label.toLowerCase() === String(target.label || '').toLowerCase() && (!target.mode || item.mode === target.mode);
          if (!match || item.mode) return item;
          changed = true;
          return createFavorite({ ...item, mode: inferFavoriteMode(item.label) });
        });
        if (!changed) return prev;
        return {
          ...prev,
          favorites: nextFavorites,
          favoriteStoreNames: nextFavorites.map((f) => f.label),
        };
      });
    },
    [setPrefs]
  );

  const removeFavorite = useCallback(
    (favoriteInput) => {
      const target = typeof favoriteInput === 'string'
        ? { label: favoriteInput }
        : favoriteInput;
      setPrefs((prev) => {
        const nextFavorites = (prev.favorites || []).filter((item) => {
          if (target.placeId && item.placeId) return item.placeId !== target.placeId;
          return !(item.label.toLowerCase() === String(target.label || '').toLowerCase() && (!target.mode || item.mode === target.mode));
        });
        return {
          ...prev,
          favorites: nextFavorites,
          favoriteStoreNames: nextFavorites.map((f) => f.label),
        };
      });
    },
    [setPrefs]
  );

  const value = useMemo(
    () => ({
      loading,
      favorites: prefs.favorites,
      favoriteStoreNames: prefs.favoriteStoreNames,
      addFavorite,
      updateFavorite,
      classifyFavorite,
      removeFavorite,
      petsFilter: prefs.petsFilter,
      setPetsFilter: (v) => setPrefs((prev) => ({ ...prev, petsFilter: v })),
      ffMinPrice: prefs.ffMinPrice,
      setFfMinPrice: (v) => setPrefs((prev) => ({ ...prev, ffMinPrice: v })),
      ffMaxPrice: prefs.ffMaxPrice,
      setFfMaxPrice: (v) => setPrefs((prev) => ({ ...prev, ffMaxPrice: v })),
      ffMinBeds: prefs.ffMinBeds,
      setFfMinBeds: (v) => setPrefs((prev) => ({ ...prev, ffMinBeds: v })),
      ffMinBaths: prefs.ffMinBaths,
      setFfMinBaths: (v) => setPrefs((prev) => ({ ...prev, ffMinBaths: v })),
      crimeLayerOpacity: prefs.crimeLayerOpacity,
      setCrimeLayerOpacity: (v) => setPrefs((prev) => ({ ...prev, crimeLayerOpacity: v })),
      showCrimeLayer: prefs.showCrimeLayer,
      setShowCrimeLayer: (v) => setPrefs((prev) => ({ ...prev, showCrimeLayer: v })),
      mapLayer: prefs.mapLayer,
      setMapLayer: (v) => setPrefs((prev) => ({ ...prev, mapLayer: v })),
      showStoreMarkers: prefs.showStoreMarkers,
      setShowStoreMarkers: (v) => setPrefs((prev) => ({ ...prev, showStoreMarkers: v })),
      showGymMarkers: prefs.showGymMarkers,
      setShowGymMarkers: (v) => setPrefs((prev) => ({ ...prev, showGymMarkers: v })),
      showThingsToDo: prefs.showThingsToDo,
      setShowThingsToDo: (v) => setPrefs((prev) => ({ ...prev, showThingsToDo: v })),
      showFFMarkers: prefs.showFFMarkers,
      setShowFFMarkers: (v) => setPrefs((prev) => ({ ...prev, showFFMarkers: v })),
      showRentals: prefs.showRentals,
      setShowRentals: (v) => setPrefs((prev) => ({ ...prev, showRentals: v })),
      showTenantCustomListings: prefs.showTenantCustomListings,
      setShowTenantCustomListings: (v) =>
        setPrefs((prev) => ({ ...prev, showTenantCustomListings: v })),
      inactiveTenantListings: prefs.inactiveTenantListings,
      toggleTenantListingInactive: (propertyUrl) =>
        setPrefs((prev) => ({
          ...prev,
          inactiveTenantListings: toggleInList(prev.inactiveTenantListings, propertyUrl),
        })),
      favoriteTenantListings: prefs.favoriteTenantListings,
      toggleTenantListingFavorite: (propertyUrl) =>
        setPrefs((prev) => ({
          ...prev,
          favoriteTenantListings: toggleInList(prev.favoriteTenantListings, propertyUrl),
        })),
      jobTitle: prefs.jobTitle,
      setJobTitle: (v) => setPrefs((prev) => ({ ...prev, jobTitle: v })),
    }),
    [loading, prefs, setPrefs, addFavorite, updateFavorite, classifyFavorite, removeFavorite]
  );

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within UserPreferencesProvider');
  }
  return context;
}

export function useFavoriteStores() {
  const p = useUserPreferences();
  return {
    favorites: p.favorites,
    favoriteStoreNames: p.favoriteStoreNames,
    addFavorite: p.addFavorite,
    updateFavorite: p.updateFavorite,
    classifyFavorite: p.classifyFavorite,
    removeFavorite: p.removeFavorite,
  };
}
